/** jkurwa's pipe flag for a `signature-time-stamp` (never `content`). */
export const SIGNING_TIMESTAMP_FLAG = "signature";

/** jkurwa's wording when no key/certificate pair can perform an operation. */
export const SIGNING_NO_KEY_PATTERN = /No key-certificate pair/i;

/** Sentinel `cmpUrl` value meaning "search the registry instead of asking the user". */
export const CA_AUTO_DETECT_VALUE = "auto";

/**
 * Hostnames that issue most keys, tried first — transcribed from the
 * jkurwa-web reference implementation (`cadetect.js`). A CMP round trip is the
 * only way to tell which ЦСК issued a key (see `ca-detect.ts`), so trying the
 * common ones first keeps the typical case to one or two batches.
 */
export const CA_DETECT_PRIORITY_HOSTNAMES = [
  "ca.tax.gov.ua",
  "acskidd.gov.ua",
  "ca.informjust.ua",
  "acsk.privatbank.ua",
  "ca.monobank.ua",
  "masterkey.ua",
  "uakey.com.ua",
  "ca.vchasno.ua",
  "ca.depositsign.com",
  "canbu.bank.gov.ua",
];

/** ЦСК probed at once — enough to be fast without flooding the gateway. */
export const CA_DETECT_BATCH_SIZE = 6;

/** Per-probe timeout: short, so one dead CMP host cannot stall the whole scan. */
export const CA_DETECT_TIMEOUT_MS = 8_000;

/**
 * Layout of the CMP lookup payload, reproduced from jkurwa's services/cmp.js
 * (its own implementation throws under ESM — see ai/docs/kep-signing.md).
 * The numbers are the wire format; they have no meaning beyond it.
 */
export const CMP_PAYLOAD_LENGTH = 120;

export const CMP_FIRST_KEY_ID_OFFSET = 0x0c;

export const CMP_SECOND_KEY_ID_OFFSET = 0x2c;

export const CMP_FLAG_OFFSETS = [0x6c, 0x70];

export const CMP_TYPE_OFFSET = 0x08;

export const CMP_TYPE_VALUE = 2;

export const CMP_HEADER_VALUE = 0x0d;

/** Position of the status word in a CMP response; 1 means "certificates follow". */
export const CMP_STATUS_OFFSET = 4;

export const CMP_STATUS_FOUND = 1;

/** The nested message with the certificates starts after the status word. */
export const CMP_PAYLOAD_OFFSET = 8;

/**
 * ЦСК encode the taxpayer number in the subject serial number as
 * `TINUA-<РНОКПП>` for individuals and `NTRUA-<ЄДРПОУ>` for companies.
 */
export const TAX_NUMBER_PREFIX_PATTERN = /^(TINUA|NTRUA)-/;

/** meedoc wraps a container in a 6-byte header starting with this byte. */
export const MEEDOC_HEADER_MARKER = 0x51;

export const MEEDOC_HEADER_LENGTH = 6;

/** Magic of a classic JKS container (Java 8 and earlier). */
export const JKS_MAGIC = "feedfeed";

/** jkurwa's wording when a container could not be parsed at all. */
export const UNPARSEABLE_KEY_STORE_PATTERN = /Cant parse store/i;

/** ІІТ convention: an address with no path means the standard CMP servlet. */
export const CMP_DEFAULT_PATH = "/services/cmp/";

// The list of ЦСК with a CMP service is machine-generated from the tax
// cabinet's own CAs.json — see CERTIFICATE_AUTHORITIES in kep-cmp-list.const.ts
// and the tools/vite/ca-registry.mjs that writes it.

/** Names the real target for the forwarding gateway. */
export const GATEWAY_TARGET_URL_HEADER = "X-Target-Url";

export const GATEWAY_DEFAULT_CONTENT_TYPE = "application/octet-stream";

export const GATEWAY_DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Fixed by the jkurwa-web gate browser extension's own `content.js` — not
 * ours to rename. See ai/docs/kep-signing.md.
 */
export const EXTENSION_GATE_MESSAGE_TAG = "jkurwa-gate";

/**
 * The extension announces itself unprompted at `document_start`, so this only
 * matters when nothing has arrived yet: how long `detectExtensionGate` waits
 * for a `hello`/`pong` before concluding it is not installed. Matches
 * `jkurwa-web/web`'s `extgate.js`.
 */
export const EXTENSION_GATE_DETECT_TIMEOUT_MS = 300;

/**
 * Extra time given to the extension's own response after its background
 * worker's fetch would have aborted, so a slow CMP/fiscal server produces the
 * extension's own error message rather than a generic "didn't answer".
 */
export const EXTENSION_GATE_RESPONSE_GRACE_MS = 2_000;

/**
 * `localStorage` key the non-sensitive metadata of a persisted session lives
 * under (gateway, expiry, when it was saved) — safe to read synchronously,
 * with no decryption. See ai/docs/kep-signing.md for what this trades away
 * and why.
 */
export const PERSISTED_KEY_SESSION_STORAGE_KEY = "prostocheck.keySession";

/** The AES-GCM ciphertext (key, certificates, password) plus its IV, both base64. */
export const PERSISTED_KEY_SESSION_CIPHERTEXT_STORAGE_KEY =
  "prostocheck.keySession.secret";

/**
 * The symmetric key that decrypts `PERSISTED_KEY_SESSION_CIPHERTEXT_STORAGE_KEY`.
 * Also `localStorage` — there is nowhere else in a static client app to keep
 * it — so this encrypts the credential at rest, not against a page that can
 * run its own JavaScript. See ai/docs/kep-signing.md.
 */
export const PERSISTED_KEY_SESSION_ENCRYPTION_KEY_STORAGE_KEY =
  "prostocheck.keySession.secretKey";

/** How often the background watcher re-checks the signed-in key's expiry. */
export const KEY_SESSION_EXPIRY_CHECK_INTERVAL_MS = 60_000;
